// demo-login.component.ts
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../../core/auth/auth.service';
import { ToastrService } from 'ngx-toastr';
import { Subject, timer } from 'rxjs';
import { timeout, catchError, takeUntil, retry } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { TidioService } from '../../../core/services/tidio.service';
import { AgencyStateService } from '../../../core/services/agency-state.service';
import { DemoRoutingService } from '../../../core/services/demo-routing.service';

@Component({
  selector: 'app-demo-login',
  template: `
    <div class="demo-login-wrapper">
      <div class="demo-login-container">
        <div class="logo-container">
          <img src="assets/images/logo/Dashboardly-logo.svg" alt="Dashboardly Logo" class="logo" />
        </div>

        @if (showChooser) {
          <div class="chooser-container">
            <h2 class="welcome-title">Try a Live Demo</h2>
            <p class="welcome-subtitle">Pick the experience that fits you</p>
            <div class="chooser-buttons">
              <button type="button" class="chooser-btn chooser-btn--brand" (click)="pick('brand')">
                <span class="chooser-btn-title">Brand Demo</span>
                <span class="chooser-btn-desc">
                  One shop. Orders, profit &amp; loss, inventory.
                </span>
              </button>
              <button
                type="button"
                class="chooser-btn chooser-btn--agency"
                (click)="pick('agency')"
              >
                <span class="chooser-btn-title">Agency Demo</span>
                <span class="chooser-btn-desc">
                  Multiple clients, shops &amp; prebuilt reports.
                </span>
              </button>
            </div>
          </div>
        } @else {
          <div class="loader-container">
            <div class="loader-7"></div>
          </div>

          <div class="text-container">
            <h2 class="welcome-title">Welcome to Demo Mode</h2>
            <p class="welcome-subtitle">Preparing your demo workspace...</p>
            <p class="welcome-description">
              You'll have full access to explore Dashboardly's features with sample data
            </p>
          </div>
        }
      </div>
    </div>
  `,
  standalone: true,
  styles: [
    `
      .demo-login-wrapper {
        min-height: 100vh;
        display: flex;
        align-items: center;
        justify-content: center;
        background: linear-gradient(135deg, #f8f9fa 0%, #e8f5f7 100%);
        padding: 20px;
      }

      .demo-login-container {
        background: white;
        border-radius: 20px;
        padding: 60px 40px;
        box-shadow: 0 10px 40px rgba(0, 167, 198, 0.1);
        text-align: center;
        max-width: 500px;
        width: 100%;
        animation: fadeInUp 0.6s ease-out;
      }

      @keyframes fadeInUp {
        from {
          opacity: 0;
          transform: translateY(30px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      .logo-container {
        margin-bottom: 40px;
      }

      .logo {
        height: 60px;
        width: auto;
        filter: drop-shadow(0 2px 8px rgba(0, 167, 198, 0.15));
      }

      .loader-container {
        margin: 40px 0;
        display: flex;
        justify-content: center;
        align-items: center;
        height: 80px;
      }

      .loader-7 {
        border: 0 solid transparent;
        border-radius: 50%;
        width: 60px;
        height: 60px;
        position: relative;
      }

      .loader-7:before,
      .loader-7:after {
        content: '';
        border: 0.2em solid #00a7c6;
        border-radius: 50%;
        width: inherit;
        height: inherit;
        position: absolute;
        top: 0;
        left: 0;
        animation: loader-pulse 2s linear infinite;
        opacity: 0;
      }

      .loader-7:before {
        animation-delay: 1s;
      }

      .loader-7:after {
        animation-delay: 0.5s;
      }

      @keyframes loader-pulse {
        0% {
          transform: scale(0);
          opacity: 0;
        }
        50% {
          opacity: 1;
        }
        100% {
          transform: scale(1);
          opacity: 0;
        }
      }

      .text-container {
        margin-top: 30px;
      }

      .welcome-title {
        font-size: 28px;
        font-weight: 700;
        color: #12263f;
        margin: 0 0 12px 0;
        letter-spacing: -0.5px;
      }

      .welcome-subtitle {
        font-size: 16px;
        font-weight: 500;
        color: #00a7c6;
        margin: 0 0 16px 0;
      }

      .welcome-description {
        font-size: 14px;
        color: #6e84a3;
        margin: 0;
        line-height: 1.6;
        max-width: 400px;
        margin-left: auto;
        margin-right: auto;
      }

      .chooser-container {
        padding: 8px 0 16px;
      }

      .chooser-buttons {
        display: flex;
        flex-direction: column;
        gap: 16px;
        margin-top: 32px;
      }

      .chooser-btn {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 6px;
        padding: 20px 24px;
        border: 2px solid #e3e9f1;
        border-radius: 12px;
        background: #ffffff;
        cursor: pointer;
        text-align: left;
        transition:
          border-color 0.2s ease,
          transform 0.2s ease,
          box-shadow 0.2s ease;
      }

      .chooser-btn:hover {
        border-color: #00a7c6;
        transform: translateY(-2px);
        box-shadow: 0 6px 18px rgba(0, 167, 198, 0.12);
      }

      .chooser-btn-title {
        font-size: 18px;
        font-weight: 700;
        color: #12263f;
      }

      .chooser-btn-desc {
        font-size: 13px;
        color: #6e84a3;
      }

      @media (max-width: 576px) {
        .demo-login-container {
          padding: 40px 24px;
        }

        .logo {
          height: 48px;
        }

        .welcome-title {
          font-size: 24px;
        }

        .welcome-subtitle {
          font-size: 14px;
        }

        .welcome-description {
          font-size: 13px;
        }

        .loader-7 {
          width: 50px;
          height: 50px;
        }
      }
    `
  ]
})
export class DemoLoginComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  showChooser = false;
  private demoLoginInFlight = false;

  constructor(
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private tidioService: TidioService,
    private agencyStateService: AgencyStateService,
    private demoRoutingService: DemoRoutingService
  ) {}

  ngOnInit() {
    // Subscribe to queryParams (not snapshot) — when the chooser navigates
    // to /demo-login?type=…, Angular reuses the same component instance and
    // never re-runs ngOnInit. The observable emits on every query-param
    // change, so we react to chooser clicks here too.
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const typeParam = params['type'];
      const validType: 'brand' | 'agency' | null =
        typeParam === 'agency' || typeParam === 'brand' ? typeParam : null;

      if (!validType) {
        // No explicit demo type — show the chooser.
        this.showChooser = true;
        return;
      }

      if (this.demoLoginInFlight) {
        // Don't double-fire if the user spam-clicks or query updates twice.
        return;
      }

      this.showChooser = false;
      this.demoLoginInFlight = true;
      this.runDemoLogin(validType);
    });
  }

  pick(type: 'brand' | 'agency'): void {
    this.router.navigate(['/demo-login'], { queryParams: { type } });
  }

  private runDemoLogin(type: 'brand' | 'agency'): void {
    // Reset cached agency state BEFORE the new token swap. Without this
    // step, switching from agency demo to brand demo (or vice versa) reuses
    // `AgencyStateService._initPromise` from the previous session — the
    // dedup keeps `isAgency`, `agencyName`, role, and reports access stale
    // (visible as brand demo loading with agency sidebar still rendered).
    // `clear()` nulls the cached promise so the next `fetchAndUpdate()`
    // hits `/users/me` fresh with the new JWT.
    this.agencyStateService.clear();

    this.authService
      .demoLogin(type)
      .pipe(
        timeout(15000), // 15 second timeout
        retry({
          count: 2,
          delay: (error, retryCount) => timer(retryCount * 1000)
        }),
        catchError(error => {
          if (error.name === 'TimeoutError') {
            this.toastr.error(
              'Demo login is taking longer than expected. Please try again later.',
              'Timeout Error',
              { timeOut: 5000 }
            );
          } else {
            this.toastr.error('Failed to login to demo mode. Please try again.', 'Error');
          }
          this.router.navigate(['/login']);
          return throwError(() => error);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: async () => {
          // Load Tidio chat widget for demo mode
          this.tidioService.loadTidioChat();

          // Hydrate agency state BEFORE navigation so guards / sidebar /
          // header recognize the demo user as agency (when applicable)
          // on the very first render. fetchAndUpdate dedupes so this is
          // safe to call even when AgencyStateService was already primed
          // elsewhere.
          try {
            await this.agencyStateService.fetchAndUpdate();
          } catch {
            // Non-fatal — agency state will still get hydrated lazily by
            // downstream guards. Keep the demo flow moving.
          }

          const target = this.demoRoutingService.getDemoLandingPath();
          this.router.navigateByUrl(target);
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
